import { useState } from "react";
import {
  Landmark,
  MoreVertical,
  Pencil,
  PlusCircle,
  Check,
  ShieldCheck,
  ShieldX,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

import { toast } from "sonner";

import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import type { PaymentMethod, PaymentType } from "@/interfaces/payment-method";
import { useCurrencies, useProfile } from "@/hooks";
import { useAppSelector } from "@/store/hooks";
import { queryClient } from "@/services/queryClient";
import { UserVerificationDialog } from "../user-component/UserVerificationDialog";
import { CustomSelect } from "../shared/CustomSelect";
import { checkPasswordSetup, removeTrailingToken } from "@/utils";
import type { UserProfile } from "@/interfaces/profile";

const paymentRoutes: PaymentType[] = [
  "BANK_TRANSFER",
  "FINTECH",
  "TELLER_PAYMENT",
];

interface PaymentForm {
  type: PaymentType | "";
  accountNumber: string;
  branchAddress: string;
  currencyId: string;
  bankId: string;
  isDefault: boolean;
}

const PaymentMethodPage = () => {
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    "create" | "update" | "toggle" | null
  >(null);

  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(
    null,
  );

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id || "";

  const { data: profile, isLoading: isLoadingProfile } = useProfile(userId);

  const { data: currencies } = useCurrencies();

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethod | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  const [form, setForm] = useState<PaymentForm>({
    type: "",
    accountNumber: "",
    branchAddress: "",
    currencyId: "",
    bankId: "",
    isDefault: false,
  });

  const fullName =
    `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim();

  const selectedCurrency =
    currencies?.find((c: any) => c.id === form.currencyId) || null;

  const handleCurrency = (option: any) => {
    setForm((prev) => ({
      ...prev,
      currencyId: option?.value ?? "",
    }));
  };

  const options = currencies?.map((currency: any) => {
    return {
      value: currency.id,
      label:
        removeTrailingToken(currency.name) + " " + "(" + currency.symbol + ")",
      iso2: currency.iso2,
    };
  });

  const formatOptionLabel = (option: any) => {
    const iso2 = option.iso2?.toLowerCase();

    console.log(iso2);

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={`https://flagcdn.com/w40/${iso2}.png`}
          alt={option.label}
          width={20}
          height={14}
          style={{ objectFit: "cover", borderRadius: 2 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  const reset = () => {
    setForm({
      type: "",
      accountNumber: "",
      branchAddress: "",
      currencyId: "",
      bankId: "",
      isDefault: false,
    });

    setEditing(null);
  };

  const openCreateModal = () => {
    if (!checkPasswordSetup(profile as UserProfile)) {
      return toast.error(
        "You must enable 2FA and setup transaction password in the setting section to continue.",
      );
    }

    reset();
    setIsOpen(true);
  };

  const handleEdit = (payment: PaymentMethod) => {
    setEditing(payment);

    setForm({
      type: payment.type ?? "",
      accountNumber: payment.accountNumber ?? "",
      branchAddress: payment.branchAddress ?? "",
      currencyId: payment.currencyId ?? "",
      bankId: payment.bankId ?? "",
      isDefault: payment.isDefault ?? false,
    });

    setIsOpen(true);
  };

  const initiateSubmit = () => {
    if (!form.type || !form.bankId || !form.accountNumber) {
      toast.warning(
        "Bank name,account number, currency and payment route are required.",
      );
      return;
    }

    if (!form.bankId || !form.currencyId) {
      toast.warning("Select currency and bank");
      return;
    }

    setPendingAction(editing ? "update" : "create");
    setVerifyOpen(true);
  };

  const handleVerifiedSubmit = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }): Promise<void> => {
    setIsLoading(true);

    try {
      if (pendingAction === "toggle" && selectedPayment) {
        await makeRequest.put(
          `/payment-method/${selectedPayment.id}/deactivate`,
          {
            otp,
            securityCode,
          },
        );

        toast.success(
          `Payment method ${
            selectedPayment.active ? "disabled" : "enabled"
          } successfully`,
        );
      } else {
        const payload = {
          type: form.type,
          accountNumber: form.accountNumber,
          branchAddress: form.branchAddress,
          bankId: form.bankId,
          currencyId: form.currencyId,
          isDefault: form.isDefault,
          otp,
          securityCode,
        };

        if (editing) {
          await makeRequest.put(
            `/payment-method/${editing.id}/update`,
            payload,
          );

          toast.success("Payment method updated");
        } else {
          await makeRequest.post("/payment-method", payload);

          toast.success("Payment method created successfully");
        }

        setIsOpen(false);
        reset();
      }

      queryClient.invalidateQueries({
        queryKey: ["profile", userId],
      });

      setPendingAction(null);
      setSelectedPayment(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const initiateToggle = (payment: PaymentMethod) => {
    setSelectedPayment(payment);
    setPendingAction("toggle");
    setVerifyOpen(true);
  };

  return (
    <div className="rounded-2xl border bg-card p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold">Payment Methods</h3>

          <p className="text-sm text-muted-foreground">
            Manage your withdrawal accounts
          </p>
        </div>

        <Button onClick={openCreateModal}>
          <PlusCircle className="w-4 h-4 mr-2" />
          Add Method
        </Button>
      </div>

      {/* PAYMENT LIST */}
      <div className="space-y-4">
        {profile?.paymentMethods.length === 0 ? (
          <div className="border border-dashed rounded-2xl p-10 text-center">
            <Landmark className="mx-auto mb-4 text-muted-foreground" />

            <h4 className="font-semibold">No payment methods yet</h4>

            <p className="text-sm text-muted-foreground mt-2">
              Add a payment method to start creating advertisements.
            </p>
          </div>
        ) : (
          profile?.paymentMethods?.map((p) => (
            <div
              key={p.id}
              className={`relative overflow-hidden rounded-2xl p-5 transition-all border ${
                p.active
                  ? "border hover:border-primary/30 bg-card"
                  : "border-red-200 bg-red-50/30"
              }`}
            >
              <div
                className={`absolute left-0 top-0 h-full w-1 ${
                  p.active ? "bg-green-500" : "bg-red-500"
                }`}
              />
              <div className="flex items-start justify-between gap-4">
                {/* LEFT */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold">{p.bank?.name}</h4>

                    <Badge variant="secondary">{p.type}</Badge>

                    {p.isDefault && (
                      <Badge className="rounded-full px-3 bg-primary/10 text-primary border border-primary/20 hover:bg-primary/10">
                        <Check className="w-3 h-3 mr-1" />
                        Default
                      </Badge>
                    )}

                    <Badge
                      className={
                        p.active
                          ? "rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                          : "rounded-full bg-red-100 text-red-700 border border-red-200 hover:bg-red-100"
                      }
                    >
                      {p.active ? (
                        <>
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <ShieldX className="w-3 h-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </div>

                  <p className="text-sm text-muted-foreground mt-3">
                    {p.accountName}
                  </p>

                  <p
                    className={`font-mono text-sm mt-1 ${
                      p.active ? "" : "text-muted-foreground"
                    }`}
                  >
                    {p.accountNumber}
                  </p>

                  <div
                    className={`mt-3 rounded-md px-3 py-2 text-xs font-medium w-fit ${
                      p.active
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {p.active
                      ? "✓ This payment method is available for advertisements and withdrawals."
                      : "⚠ This payment method is disabled and cannot be used until it is enabled again."}
                  </div>

                  {p.branchAddress && (
                    <p className="text-sm text-muted-foreground mt-3">
                      {p.branchAddress}
                    </p>
                  )}
                </div>

                {/* RIGHT */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="outline">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={!p.active}
                      onClick={() => handleEdit(p)}
                    >
                      <Pencil className="mr-2 w-4 h-4 disabled:cursor-not-allowed" />
                      Edit
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => initiateToggle(p)}
                      className={
                        p.active
                          ? "text-amber-600 focus:text-amber-600"
                          : "text-green-600 focus:text-green-600"
                      }
                    >
                      {p.active ? (
                        <>
                          <ShieldX className="mr-2 h-4 w-4" />
                          Disable
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="mr-2 h-4 w-4" />
                          Enable
                        </>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {/* {!p.active && (
                <div className="absolute top-4 right-14 rounded-full bg-red-600 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
                  Disabled
                </div>
              )} */}
            </div>
          ))
        )}
      </div>

      {/* MODAL */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit Payment Method" : "Add Payment Method"}
            </DialogTitle>

            <DialogDescription>
              Configure your withdrawal account details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* ACCOUNT NAME */}
            <div className="">
              <Label>Account Name</Label>

              <div className="h-11 flex items-center rounded-md  border bg-muted/40 px-3 text-sm text-muted-foreground ">
                {fullName}
              </div>
            </div>

            {/* CURRENCY */}
            <div className="">
              <Label className="mb-2">Fiat Currency</Label>

              {/* <Select
                value={form.currencyId}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    currencyId: value,
                  }))
                }
              >
                <SelectTrigger className="w-full !h-11">
                  <SelectValue placeholder="Select fiat currency" />
                </SelectTrigger>

                <SelectContent>
                  {currencies?.map((currency) => (
                    <SelectItem
                      key={currency.id}
                      value={currency.id}
                      className="py-3"
                    >
                      {currency.iso} ({currency.symbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select> */}

              <CustomSelect
                options={options}
                isClearable
                isSearchable
                value={
                  options?.find((opt: any) => opt.value === form.currencyId) ||
                  null
                }
                onChange={handleCurrency}
                formatOptionLabel={formatOptionLabel}
                getOptionLabel={(option: any) => option.label}
                getOptionValue={(option: any) => option.value}
              />
            </div>

            {/* BANK NAME */}
            <div className="">
              <Label className="mb-2">Bank Name</Label>

              <Select
                value={form.bankId}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    bankId: value,
                  }))
                }
                disabled={
                  !form.currencyId || selectedCurrency?.banks?.length === 0
                }
              >
                <SelectTrigger className="w-full !h-12 disabled:cursor-not-allowed">
                  <SelectValue placeholder="Select bank" />
                </SelectTrigger>

                <SelectContent>
                  {selectedCurrency?.banks?.map((bank: any) => (
                    <SelectItem key={bank.id} value={bank.id} className="py-3">
                      {bank.name}
                    </SelectItem>
                  ))}
                </SelectContent>
                {selectedCurrency?.banks?.length === 0 && (
                  <span className="text-[12px] text-red-500">
                    No payment methods for this currency yet!
                  </span>
                )}
              </Select>
            </div>

            {/* PAYMENT TYPE */}
            <div className="">
              <Label className="mb-2">Payment Route</Label>

              <Select
                value={form.type}
                onValueChange={(value: PaymentType) =>
                  setForm((prev) => ({
                    ...prev,
                    type: value,
                  }))
                }
                disabled={
                  !form.currencyId ||
                  selectedCurrency?.banks?.length === 0 ||
                  !form.bankId
                }
              >
                <SelectTrigger className="w-full !h-12">
                  <SelectValue placeholder="Select payment route" />
                </SelectTrigger>

                <SelectContent>
                  {paymentRoutes.map((route) => (
                    <SelectItem key={route} value={route} className="py-3">
                      {route}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ACCOUNT NUMBER */}
            <div className="">
              <Label className="mb-2">Account Number</Label>

              <Input
                className="h-12"
                type="number"
                placeholder="0123456789"
                value={form.accountNumber}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    accountNumber: e.target.value,
                  }))
                }
              />
            </div>

            {/* BRANCH ADDRESS */}
            <div className="">
              <Label className="mb-2">
                Branch Address
                <span className="text-muted-foreground ml-1">(Optional)</span>
              </Label>

              <Textarea
                placeholder="Enter branch address"
                value={form.branchAddress}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    branchAddress: e.target.value,
                  }))
                }
              />
            </div>

            {/* DEFAULT TOGGLE */}
            <div className="flex items-center justify-between gap-4 rounded-xl border p-4">
              <div>
                <Label className="text-sm font-medium">
                  Default Payment Method
                </Label>

                <p className="text-xs text-muted-foreground mt-1">
                  Automatically use this payment method for trades and
                  withdrawals.
                </p>
              </div>

              <Button
                type="button"
                size="sm"
                variant={form.isDefault ? "default" : "outline"}
                className="rounded-full min-w-[110px]"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    isDefault: !prev.isDefault,
                  }))
                }
              >
                {form.isDefault ? "Default" : "Set Default"}
              </Button>
            </div>

            {/* ACTIONS */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>

              <Button disabled={isLoading} onClick={initiateSubmit}>
                {isLoading ? "Loading..." : editing ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <UserVerificationDialog
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        loading={isLoading}
        onVerify={handleVerifiedSubmit}
      />
    </div>
  );
};

export default PaymentMethodPage;
